import { useState, useCallback, useEffect } from 'react';
import { useQuery, useLazyQuery } from '@apollo/client';
import { GET_ACTIVITY_COUNTS, GET_ACTIVITY_DATA } from '../queries/activityQueries';

export interface ActivityItem {
  id: string;
  title?: string;
  deal?: {
    id: string;
    title: string;
  };
  dealId?: string;
  status?: string;
  signers?: { email: string }[];
  invitationSentBy?: { fullName: string };
}

// Map server activity items to the frontend ActivityItem shape
const mapActivityItems = (items: any[] | undefined): ActivityItem[] => {
  if (!items) return [];
  return items.map((item: any) => ({
    id: item.id,
    title: item.title,
    status: item.status,
    dealId: item.dealId,
    deal: item.dealId ? { id: item.dealId, title: item.dealTitle || 'Untitled Deal' } : undefined,
    signers: item.signerEmails?.map((email: string) => ({ email })),
    invitationSentBy: item.invitationSentByName ? { fullName: item.invitationSentByName } : undefined,
  }));
};

export const useActivityData = (teamId: string) => {
  const [detailsLoaded, setDetailsLoaded] = useState(false);

  // Lightweight counts query — fires immediately, returns fast.
  const {
    data: countsData,
    loading: countsLoading,
    refetch: refetchCounts,
  } = useQuery(GET_ACTIVITY_COUNTS, {
    variables: { teamId },
    skip: !teamId,
    pollInterval: 30000,
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
  });

  // Reset detail state when team changes
  useEffect(() => {
    setDetailsLoaded(false);
  }, [teamId]);

  // Detail data — lazy, only fires when the dropdown is opened.
  const [
    fetchDetails,
    { data: detailsData, loading: detailsLoading },
  ] = useLazyQuery(GET_ACTIVITY_DATA, {
    fetchPolicy: 'network-only',
    errorPolicy: 'all',
    onCompleted: () => setDetailsLoaded(true),
  });

  // Counts from the lightweight server query above.
  const counts = {
    pendingApprovalsCount: countsData?.getActivityCounts?.pendingApprovalsCount ?? 0,
    dealInvitationsCount: countsData?.getActivityCounts?.dealInvitationsCount ?? 0,
    documentsInReviewCount: countsData?.getActivityCounts?.documentsInReviewCount ?? 0,
    documentsInSigningPrepCount: countsData?.getActivityCounts?.documentsInSigningPrepCount ?? 0,
    documentsOutForSigningCount: countsData?.getActivityCounts?.documentsOutForSigningCount ?? 0,
  };

  // Map server-side filtered data to frontend shape
  const activityData = detailsData?.getActivityData;

  // Fetch detail data on demand (re-fetches after team switch resets detailsLoaded)
  const loadDetails = useCallback(async () => {
    if (!detailsLoaded) {
      await fetchDetails({ variables: { teamId } });
    }
  }, [fetchDetails, detailsLoaded, teamId]);

  // Refetch: refresh counts + force-refresh detail data
  const refetch = useCallback(async () => {
    await Promise.all([
      refetchCounts?.(),
      fetchDetails({ variables: { teamId } }),
    ]);
  }, [refetchCounts, fetchDetails, teamId]);

  return {
    pendingApprovals: mapActivityItems(activityData?.pendingApprovals),
    dealInvitations: mapActivityItems(activityData?.dealInvitations),
    documentsInReview: mapActivityItems(activityData?.documentsInReview),
    documentsInSigningPrep: mapActivityItems(activityData?.documentsInSigningPrep),
    documentsOutForSigning: mapActivityItems(activityData?.documentsOutForSigning),
    loading: countsLoading,
    isProcessing: detailsLoading,
    refetch,
    loadDetails,
    counts,
    detailsLoaded,
  };
};
